import React from "react";
import { useTranslation } from "react-i18next";

function ConditionPage() {
  const { t } = useTranslation("global");
  
  const sections = [
    { title: t("conditions.1"), content: t("conditions.1.1") },
    { title: t("conditions.2"), content: t("conditions.2.2") },
    { title: t("conditions.3"), content: t("conditions.3.3") },
    { title: t("conditions.4"), content: t("conditions.4.4") },
    { title: t("conditions.5"), content: t("conditions.5.5") },
    { title: t("conditions.6"), content: t("conditions.6.6") },
    { title: t("conditions.7"), content: t("conditions.7.7") },
    { title: t("conditions.8"), content: t("conditions.8.8") }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-blue-800 to-blue-900 text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="md:w-3/5 space-y-6">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold">
                {t("conditions.title")}
              </h1>
              <p className="text-xl text-blue-100">
                {t("conditions.subtitle", "Ces conditions régissent votre utilisation de la plateforme ISOeVAL")}
              </p>
              <div className="w-20 h-1 bg-blue-300"></div>
            </div>
            
            <div className="md:w-2/5">
              {/* Terms & Conditions Document Design - Tailwind Only */}
              <div className="relative w-64 h-64 mx-auto">
                {/* Background element */}
                <div className="absolute w-full h-full bg-blue-700 rounded-full opacity-20"></div>
                
                {/* Document design with stacked papers */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-56 bg-white rounded-lg shadow-xl rotate-6">
                  {/* Document lines */}
                  <div className="absolute top-12 left-4 w-32 h-2 bg-gray-200 rounded-full"></div>
                  <div className="absolute top-18 left-4 w-36 h-2 bg-gray-200 rounded-full"></div>
                  <div className="absolute top-24 left-4 w-28 h-2 bg-gray-200 rounded-full"></div>
                  <div className="absolute top-30 left-4 w-34 h-2 bg-gray-200 rounded-full"></div>
                  <div className="absolute top-36 left-4 w-30 h-2 bg-gray-200 rounded-full"></div>
                </div>
                
                {/* Main document */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-56 bg-blue-50 rounded-lg shadow-xl -rotate-3">
                  {/* Document header */}
                  <div className="absolute top-0 left-0 w-full h-10 bg-blue-600 rounded-t-lg"></div>
                  
                  {/* Document content */}
                  <div className="absolute top-14 left-4 w-36 h-2 bg-blue-200 rounded-full"></div>
                  <div className="absolute top-20 left-4 w-32 h-2 bg-blue-200 rounded-full"></div>
                  <div className="absolute top-26 left-4 w-36 h-2 bg-blue-200 rounded-full"></div>
                  <div className="absolute top-32 left-4 w-28 h-2 bg-blue-200 rounded-full"></div>
                  <div className="absolute top-38 left-4 w-36 h-2 bg-blue-200 rounded-full"></div>
                  
                  {/* Signature line */}
                  <div className="absolute bottom-8 right-6 w-20 h-0.5 bg-blue-400"></div>
                </div>
                
                {/* Foreground document with terms icon */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-56 bg-white rounded-lg shadow-xl">
                  {/* Document header */}
                  <div className="absolute top-0 left-0 w-full h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-t-lg flex items-center justify-center">
                    <div className="text-white font-bold text-sm">{t("conditions.title", "Conditions")}</div>
                  </div>
                  
                  {/* Document content lines */}
                  <div className="absolute top-14 left-4 w-40 h-2 bg-gray-200 rounded-full"></div>
                  <div className="absolute top-20 left-4 w-36 h-2 bg-gray-200 rounded-full"></div>
                  <div className="absolute top-26 left-4 w-40 h-2 bg-gray-200 rounded-full"></div>
                  <div className="absolute top-32 left-4 w-32 h-2 bg-gray-200 rounded-full"></div>
                  <div className="absolute top-38 left-4 w-38 h-2 bg-gray-200 rounded-full"></div>
                  
                  {/* Document icon */}
                  <div className="absolute bottom-5 right-5 w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
                
                {/* Decorative elements */}
                <div className="absolute w-10 h-10 bg-blue-400 rounded-full -top-4 right-8 opacity-40"></div>
                <div className="absolute w-6 h-6 bg-blue-300 rounded-full top-1/3 -right-2 opacity-60"></div>
                <div className="absolute w-8 h-8 bg-blue-300 rounded-full bottom-0 left-10 opacity-50"></div>
                
                {/* Floating mini elements */}
                <div className="absolute top-6 -left-4 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center transform -rotate-12 shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                  </svg>
                </div>
                
                <div className="absolute bottom-8 right-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center transform rotate-12 shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Terms Content Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-8">
              <div className="flex items-center mb-8">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-heading font-bold text-gray-900">
                  {t("conditions.introduction", "Introduction aux conditions d'utilisation")}
                </h2>
              </div>
              
              <p className="text-gray-700 mb-6">
                {t("conditions.introText", "Veuillez lire attentivement ces conditions d'utilisation avant d'utiliser notre plateforme. En accédant à notre service, vous acceptez d'être lié par ces termes.")}
              </p>
              
              <div className="space-y-8">
                {sections.map((section, index) => (
                  <div key={index} className="p-6 bg-gray-50 rounded-lg">
                    <h3 className="text-xl font-heading font-bold text-blue-900 mb-3">
                      {section.title}
                    </h3>
                    <div className="w-16 h-0.5 bg-blue-600 mb-4"></div>
                    <p className="text-gray-700">
                      {section.content}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Contact CTA Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">
              {t("conditions.questions", "Vous avez des questions sur nos conditions?")}
            </h2>
            <p className="text-gray-600 mb-8">
              {t("conditions.contactUs", "Si vous avez des questions concernant nos conditions d'utilisation, n'hésitez pas à nous contacter.")}
            </p>
            <a
              href="contact"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white py-3 px-8 rounded-full font-heading font-bold transition-all duration-300"
            >
              {t("contact.contact")}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

export default ConditionPage;